// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useCallback, useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { WizardLayout }   from '@/modules/wizard/WizardLayout'
import { useWizardStore, useAuthStore } from '@/store'
import { loadTrustedPersons } from '@/services/trustedPerson'
import { STORAGE_KEY }    from '@/store/wizardStore'
import { profileExists }  from '@/services/profile'
import { ROUTES, HOME_ROUTE } from '@/lib/constants/routes'
import type { SaveStatus } from '@/hooks/useAutosave'
import { Shield, ArrowRight, Info } from 'lucide-react'

import { Step01PersonalData }     from '@/modules/wizard/steps/Step01PersonalData'
import { Step02Partner }          from '@/modules/wizard/steps/Step02Partner'
import { NewStep01TrustedPerson } from '@/modules/wizard/steps/NewStep01TrustedPerson'
import { NewStep02Children }      from '@/modules/wizard/steps/NewStep02Children'
import { NewStep03Pets }          from '@/modules/wizard/steps/NewStep03Pets'
import { NewStep04Housing }       from '@/modules/wizard/steps/NewStep04Housing'
import { NewStep05SecondHome }    from '@/modules/wizard/steps/NewStep05SecondHome'
import { NewStep06ExtraLocations } from '@/modules/wizard/steps/NewStep06ExtraLocations'
import { NewStep06Business }      from '@/modules/wizard/steps/NewStep06Business'
import { NewStep07Investments }   from '@/modules/wizard/steps/NewStep07Investments'
import { Step07Financial }        from '@/modules/wizard/steps/Step07Financial'
import { NewStep08Donor }         from '@/modules/wizard/steps/NewStep08Donor'
import { NewStep09Testament }     from '@/modules/wizard/steps/NewStep09Testament'
import { NewStep10LivingWill }    from '@/modules/wizard/steps/NewStep10LivingWill'
import { NewStep11FuneralWishes } from '@/modules/wizard/steps/NewStep11FuneralWishes'
import { NewStep12FuneralCosts }  from '@/modules/wizard/steps/NewStep12FuneralCosts'
import { NewStep12Medical }       from '@/modules/wizard/steps/NewStep12Medical'
import { NewStep13Messages }      from '@/modules/wizard/steps/NewStep13Messages'
import { NewStep14Review }        from '@/modules/wizard/steps/NewStep14Review'
import { VehiclesPage }           from '@/pages/vehicles/VehiclesPage'
import { AIGuidePage }            from '@/pages/ai-guide/AIGuidePage'
import { DocumentIndexPage }      from '@/pages/document-index/DocumentIndexPage'
import { PersonalFarewellPage }   from '@/pages/wishes/PersonalFarewellPage'

// 17 stappen totaal - welkomstscherm telt NIET als stap
const TOTAL_STEPS = 23

// ── Welkomstscherm ─────────────────────────────────────────────────────────────

const WELCOME_CARDS = [
  { emoji: '👨‍👩‍👧', bg: 'bg-red-50',    title: 'Uw gezin en naasten',
    desc:  'Uw nabestaanden weten wie belangrijk voor u was.' },
  { emoji: '🤝',    bg: 'bg-sage-50',  title: 'Uw vertrouwenspersoon',
    desc:  'Er is iemand die uw naasten direct kan helpen als eerste contactpersoon.' },
  { emoji: '🏥',    bg: 'bg-orange-50',title: 'Uw wensen rondom zorg',
    desc:  'Uw naasten weten wat u wel en niet wilt.' },
  { emoji: '🌸',    bg: 'bg-pink-50',  title: 'Uw wensen voor afscheid',
    desc:  'Uw naasten weten hoe u herinnerd wilt worden.' },
  { emoji: '📁',    bg: 'bg-amber-50', title: 'Waar liggen de papieren?',
    desc:  'Uw naasten hoeven nergens meer naar te zoeken.' },
  { emoji: '✉️',    bg: 'bg-blue-50',  title: 'Persoonlijke berichten',
    desc:  'Uw naasten ontvangen uw eigen woorden op het juiste moment.' },
]

function WelcomeScreen({ onStart, onBack }: { onStart: () => void; onBack: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-sand-200">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          <span>Terug</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-moss">
            <Shield className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-bold text-ink">WilsApp</span>
        </div>
        <div className="w-16" />{/* spacer for centering */}
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-7 max-w-lg mx-auto w-full flex flex-col gap-5">

        {/* Introductie */}
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-ink leading-snug">
            WilsApp is geen digitale ordner
          </h1>
          <p className="text-base text-ink-2 leading-relaxed">
            Veel mensen denken dat ze alles goed geregeld hebben. Totdat ze ontdekken
            hoeveel belangrijke zaken ze nog nooit hebben overwogen.
          </p>
          <p className="text-base text-ink-2 leading-relaxed">
            WilsApp helpt u stap voor stap, zodat u rustig kunt vastleggen wat voor u belangrijk is, nu u dat zelf nog kunt bepalen.
          </p>
          <p className="text-base text-ink-2 leading-relaxed">
            Onderweg denkt u misschien wel eens:
          </p>
          <p className="text-lg font-semibold text-ink italic leading-snug">
            &ldquo;Dat wist ik eigenlijk ook niet.&rdquo;
          </p>
          <p className="text-base text-ink-2 leading-relaxed">
            Dat is precies waar WilsApp voor bedoeld is.
          </p>
        </div>

        {/* Geruststelling */}
        <div className="rounded-2xl bg-sage-50 border border-sage-200 px-5 py-4 flex flex-col gap-2.5">
          {[
            { emoji: '✅', text: 'Niets is verplicht' },
            { emoji: '⏸️', text: 'U kunt tussendoor stoppen' },
            { emoji: '🔄', text: 'U kunt altijd later verdergaan' },
            { emoji: '⏱️', text: 'Binnen 10 tot 15 minuten zet u de eerste belangrijke stap' },
          ].map(item => (
            <div key={item.text} className="flex items-center gap-2.5">
              <span className="text-base flex-shrink-0">{item.emoji}</span>
              <span className="text-sm text-sage-800">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Kaarten - wat levert het op */}
        <div>
          <p className="text-sm font-semibold text-ink mb-3">Wat legt u vast voor uw naasten:</p>
          <div className="flex flex-col gap-2">
            {WELCOME_CARDS.map((card, i) => (
              <div key={i} className={`flex items-start gap-3 rounded-xl ${card.bg} border border-sand-200 px-4 py-3`}>
                <span className="text-xl flex-shrink-0 mt-0.5">{card.emoji}</span>
                <div>
                  <p className="text-sm font-semibold text-ink">{card.title}</p>
                  <p className="text-xs text-ink-3 mt-0.5 leading-relaxed">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-ink-4 text-center leading-relaxed">
          WilsApp helpt u bij de voorbereiding. Voor officiële juridische of notariële
          vastlegging blijft een notaris of andere bevoegde professional nodig.
        </p>

        {/* Vertrouwensbalk - subtiel, geen waarschuwing, geen logo's/overheidssuggestie */}
        <div className="flex items-start gap-2.5 rounded-xl bg-sand border border-sand-300 px-4 py-3">
          <Info className="h-4 w-4 text-ink-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-ink-3 leading-relaxed">
            De stappen en informatie in WilsApp zijn samengesteld op basis van openbare
            informatie, wet- en regelgeving en inzichten uit de praktijk van overheid en
            ketenpartners zoals notarissen, uitvaartorganisaties, verzekeraars en andere
            deskundige organisaties.
          </p>
        </div>

        {/* Wilsbekwaamheid - rode draad, informatief, geen juridische strekking */}
        <p className="text-xs text-ink-4 text-center leading-relaxed">
          WilsApp gaat ervan uit dat u deze keuzes maakt op een moment dat u dat zelf nog bewust
          en weloverwogen kunt doen. Door gegevens vast te leggen, bevestigt u dat u deze
          informatie vrijwillig en naar eigen inzicht invult.
        </p>

      </div>

      {/* Vaste knop onderaan */}
      <div className="sticky bottom-0 bg-white border-t border-sand-200 px-5 py-4">
        <button
          onClick={onStart}
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-moss text-white py-4 text-base font-semibold hover:bg-moss-light active:scale-[0.98] transition-all shadow-md"
        >
          Start mijn voorbereiding
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

// ── Wizard ─────────────────────────────────────────────────────────────────────

export function GuidedWizardPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { currentStep, nextStep, prevStep, markCompleted } = useWizardStore()
  // V40B-P1: saveRef - WizardStepShell schrijft hier saveNow in; nav-functies roepen het aan
  const stepSaveRef = useRef<(() => Promise<void>) | null>(null)
  const [saveStatus,   setSaveStatus]   = useState<SaveStatus>('idle')
  const [vpFormOpen,   setVpFormOpen]   = useState(false)
  const [vpLaterChosen, setVpLaterChosen] = useState(false)
  const [vpHasVP,      setVpHasVP]      = useState(false)
  const { userId } = useAuthStore()

  // V36-P1 bugfix: initialiseer vpHasVP vanuit DB zodat Volgende actief blijft na terugnavigatie
  useEffect(() => {
    if (!userId) return
    loadTrustedPersons(userId).then(persons => {
      if (persons.length > 0) setVpHasVP(true)
    })
  }, [userId])

  // WelcomeScreen tonen als:
  // 1. ?welcome=1 in de URL staat (bewust: "Terug naar introductie"), OF
  // 2. de wizard nog nooit gestart is (STORAGE_KEY bestaat niet in localStorage)
  const forceWelcome = new URLSearchParams(location.search).get('welcome') === '1'
  const hasStarted   = localStorage.getItem(STORAGE_KEY) !== null
  const [showWelcome, setShowWelcome]  = useState(forceWelcome || !hasStarted)

  const handleSaveStatus = useCallback((s: SaveStatus) => setSaveStatus(s), [])

  async function handleNext() {
    setVpFormOpen(false)   // reset bij navigatie weg van VP-stap
    // V40B-P1: sla pending autosave op vóór stap-navigatie (race condition fix)
    if (stepSaveRef.current) await stepSaveRef.current()
    if (currentStep === TOTAL_STEPS - 1) {
      markCompleted()
      navigate(ROUTES.DASHBOARD)
    } else {
      nextStep()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  async function handlePrev() {
    setVpFormOpen(false)   // reset bij navigatie weg van VP-stap
    // V40B-P1: sla pending autosave op vóór stap-navigatie (race condition fix)
    if (stepSaveRef.current) await stepSaveRef.current()
    if (currentStep === 0) {
      setShowWelcome(true)
      return
    }
    prevStep()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // On mount: stap 0 overslaan als profiel al bestaat (onboarding heeft data al verzameld).
  useEffect(() => {
    const stored = localStorage.getItem('wilsapp_wizard_step')
    if (!stored) {
      // Nieuwe gebruiker: check of profiel al persoonsgegevens bevat
      profileExists().then(exists => {
        // Als profiel bestaat → begin bij stap 1 (Partner), niet stap 0
        useWizardStore.getState().setStep(exists ? 1 : 0)
      })
    } else {
      // Terugkerende gebruiker: als hij op stap 0 staat en profiel bestaat → schuif door
      const step = parseInt(stored, 10)
      if (step === 0) {
        profileExists().then(exists => {
          if (exists) useWizardStore.getState().setStep(1)
        })
      }
    }
  }, [])

  const handleVpFormOpen  = useCallback((open: boolean) => setVpFormOpen(open), [])
  const handleVpLaterChosen = useCallback(() => setVpLaterChosen(true), [])
  const handleVpCreated     = useCallback(() => setVpHasVP(true), [])
  const p = { onSaveStatus: handleSaveStatus, saveRef: stepSaveRef }

  const stepContent = (() => {
    switch (currentStep) {
      // ── Persoonlijk & netwerk ──────────────────────────────────────────────
      case 0:  return <Step01PersonalData     {...p} />  // Persoonlijke gegevens
      case 1:  return <Step02Partner          {...p} />  // Partner
      case 2:  return <NewStep01TrustedPerson {...p} onFormOpen={handleVpFormOpen} onLaterChosen={handleVpLaterChosen} onVpCreated={handleVpCreated} />  // Vertrouwenspersoon
      case 3:  return <NewStep02Children      {...p} />  // Kinderen & voogdij
      case 4:  return <NewStep03Pets          {...p} />  // Huisdieren
      // ── Bezittingen ────────────────────────────────────────────────────────
      case 5:  return <NewStep04Housing       {...p} />  // Wonen
      case 6:  return <NewStep05SecondHome    {...p} />  // Tweede woning
      case 7:  return <NewStep06ExtraLocations {...p} />  // Extra locaties
      case 8:  return <NewStep06Business      {...p} />  // Onderneming
      case 9:  return <VehiclesPage saveRef={stepSaveRef} />  // Voertuigen & mobiliteit
      case 10: return <Step07Financial        {...p} />  // Financieel
      case 11: return <NewStep07Investments   {...p} />  // Aandelen & beleggingen
      // ── Persoonlijke wensen ─────────────────────────────────────────────────
      case 12: return <NewStep08Donor         {...p} />  // Donorregistratie
      case 13: return <NewStep12Medical       {...p} />  // Medische wensen
      case 14: return <NewStep09Testament     {...p} />  // Testament
      case 15: return <NewStep10LivingWill    {...p} />  // Levenstestament
      case 16: return <NewStep11FuneralWishes {...p} />  // Uitvaartwensen
      case 17: return <NewStep12FuneralCosts   {...p} />  // Uitvaartverzekering & kosten
      // ── Nalatenschap & afsluiting ────────────────────────────────────────────
      case 18: return <AIGuidePage embedded saveRef={stepSaveRef} />          // Digitale nalatenschap
      case 19: return <DocumentIndexPage embedded />                                    // Documentwijzer
      case 20: return <PersonalFarewellPage embedded saveRef={stepSaveRef} /> // Persoonlijk afscheid
      case 21: return <NewStep13Messages      {...p} />  // Berichtenservice
      case 22: return <NewStep14Review        {...p} />  // Controle & afronding
      default: return null
    }
  })()

  // Welkomstscherm tonen vóór de wizard
  if (showWelcome) {
    return <WelcomeScreen onStart={() => setShowWelcome(false)} onBack={() => navigate(HOME_ROUTE)} />
  }

  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      saveStatus={saveStatus}
      canNext={!vpFormOpen && (currentStep !== 2 || vpLaterChosen || vpHasVP)}
      hideNav={vpFormOpen}
      onNext={handleNext}
      onPrev={handlePrev}
    >
      {stepContent}
    </WizardLayout>
  )
}
